import React from 'react';
import {
	Button,
	Flex,
	IconButton,
	Link,
	useDisclosure,
	VStack,
	Modal as ChakraModal,
	ModalOverlay,
	ModalContent,
	HStack,
	Box,
} from '@tempo/core';
import {ReorderIcon, CrossIcon} from '@tempo/icons';
import {NavLink} from '@tempo/nextjs';
import DeezerLogo from '@components/logos/DeezerLogo';

type ModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

const navItems = [
	{label: 'Plans', href: 'https://www.deezer.com/offers'},
	{label: 'Features', href: 'https://www.deezer.com/explore/features'},
	{label: 'Music', href: 'https://www.deezer.com/channels/explore'},
];

const authItems = [
	{label: 'Log In', href: 'https://account.deezer.com/login'},
	{label: 'Sign Up', href: 'https://account.deezer.com/signup'},
];

const allNavItems = [...authItems, ...navItems];

const Header = () => {
	const {isOpen, onOpen, onClose} = useDisclosure();

	return (
		<Box
			borderBottomWidth={1}
			borderBottomColor="text.neutral.tertiary.default"
			pb={{base: 'spacing.m', md: 'spacing.l'}}
		>
			<Flex
				align="center"
				justify="space-between"
				w={{base: '100%', md: '80%'}}
				mx="auto"
			>
				<DeezerLogo />
				{/* Desktop Navigation */}
				<HStack spacing="spacing.xl" display={{base: 'none', md: 'flex'}}>
					{/* Navigation Links */}
					<HStack spacing="spacing.m">
						{navItems.map((item) => (
							<NavLink key={item.label} href={item.href}>
								<Button size="md" variant="onDark">
									{item.label}
								</Button>
							</NavLink>
						))}
					</HStack>

					{/* Authentication Buttons */}
					<HStack spacing="spacing.m">
						<NavLink href={authItems[0].href}>
							<Button size="md" variant="outline">
								{authItems[0].label}
							</Button>
						</NavLink>
						<NavLink href={authItems[1].href}>
							<Button size="md">{authItems[1].label}</Button>
						</NavLink>
					</HStack>
				</HStack>

				{/* Mobile Navigation */}
				<HStack spacing="spacing.l" display={{base: 'flex', md: 'none'}}>
					<IconButton
						icon={<ReorderIcon />}
						variant="onDark"
						aria-label="Open menu"
						onClick={onOpen}
					/>
				</HStack>
			</Flex>
			<Modal isOpen={isOpen} onClose={onClose} />
		</Box>
	);
};

const Modal = ({isOpen, onClose}: ModalProps) => {
	return (
		<ChakraModal isOpen={isOpen} onClose={onClose} size="full">
			<ModalOverlay />
			<ModalContent>
				<Flex align="center" justify="space-between" p="spacing.l">
					<DeezerLogo />
					<IconButton
						icon={<CrossIcon />}
						variant="ghost"
						aria-label="Close menu"
						onClick={onClose}
					/>
				</Flex>

				<VStack spacing="spacing.xl" mt="spacing.xl" align="center">
					{allNavItems.map((item) => (
						<Link
							key={item.label}
							href={item.href}
							onClick={onClose}
							fontSize="lg"
							_hover={{color: 'text.accent.primary.hovered'}}
						>
							{item.label}
						</Link>
					))}
				</VStack>
			</ModalContent>
		</ChakraModal>
	);
};

export default Header;
